/**
 * Deploy-skew recovery for lazy chunks.
 *
 * Every deploy replaces the content-hashed files in /assets. A tab that was
 * opened before the deploy still references the old hashes, so its next lazy
 * import 404s (the SPA fallback serves index.html → strict-MIME TypeError)
 * and the route crashes to an error boundary. Reloading fetches the new
 * index.html and fixes it — so we do that automatically, once, when Vite
 * reports a preload failure.
 */

const CHUNK_ERROR_PATTERNS = [
  /failed to fetch dynamically imported module/i, // Chrome
  /error loading dynamically imported module/i, // Firefox
  /importing a module script failed/i, // Safari
  /unable to preload css/i, // Vite preload helper
];

export const CHUNK_RELOAD_GUARD_KEY = "storemandu:chunk-reload-at";

/** A repeat failure within this window means the chunk is genuinely missing
 * (broken deploy), not stale — reloading again would just loop. */
const RELOAD_GUARD_WINDOW_MS = 30_000;

export function isChunkLoadError(error: unknown): boolean {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : "";
  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

function shouldReload(): boolean {
  const now = Date.now();
  let lastReloadAt = 0;
  try {
    lastReloadAt = Number(sessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY)) || 0;
  } catch {
    /* sessionStorage unavailable — reload anyway; worst case the boundary catches round two */
  }
  if (now - lastReloadAt < RELOAD_GUARD_WINDOW_MS) {
    return false;
  }
  try {
    sessionStorage.setItem(CHUNK_RELOAD_GUARD_KEY, String(now));
  } catch {
    /* ignore */
  }
  return true;
}

/**
 * Listen for Vite's `vite:preloadError` and reload once so the browser picks
 * up the freshly deployed index.html. When the guard blocks (second failure
 * within 30s), the event is left unprevented so Vite rethrows and the route
 * error boundary takes over. Returns an uninstall function (used by tests).
 * (`reload` is injectable because jsdom's window.location is unforgeable.)
 */
export function installChunkReloadHandler(
  reload: () => void = () => window.location.reload(),
): () => void {
  const onPreloadError = (event: Event) => {
    if (shouldReload()) {
      event.preventDefault();
      reload();
    }
  };
  window.addEventListener("vite:preloadError", onPreloadError);
  return () => window.removeEventListener("vite:preloadError", onPreloadError);
}
